<template>
  <b-sidebar
    shadow
    visible
    backdrop
    no-header
    :class="{'show': paymentSource, 'd-none': !paymentSource}"
    sidebar-class="cp-sidebar ps-sidebar"
    body-class="cp-sidebar__body"
    @change="$emit('clear')"
    :no-close-on-esc="isCPLoading"
    :no-close-on-backdrop="isCPLoading"
  >
    <SidebarHeader :title="'Payment Method'" @clear="!isCPLoading && $emit('clear')" />
    <div class="cp-sidebar__main" :class="{'cp-sidebar__loader': isCPLoading}">
      <b-alert class="mb-0 w-100" show dismissible v-if="cardErrors.error" variant="danger">
        <div class="text-nowrap" v-html="cardErrors.error"></div>
      </b-alert>
      <ProcessingGif :processing="isCPLoading" />
      <template v-if="!isCPLoading">
        <b-form class="cp-sidebar__main__wrapper">
          <div class="card-container">
            <div v-if="canUpdateCardInfo">
              <b-form-group class="cp__form__field form__input mb-0" label-class="cp__form__field__label" label="Card number" label-for="number">
                <img class="left-icon" :src="require(`@/assets/images/${(getCardIcon(cardBrand.toLowerCase()))}.svg`)" alt=""/>
                <b-form-input id="number" type="tel" ref="number" class="form-control w-100"
                              :data-error="(cardErrors.number) ? true : false"
                              placeholder="1234 1234 1234 2378" :maxlength="19"
                              v-model.trim="number" v-cardformat:formatCardNumber>
                </b-form-input>
                <div class="right-icon">
                  <img id="safe-and-secure" :src="require('@/assets/images/lock.svg')"/>
                  <b-tooltip target="safe-and-secure" triggers="hover" placement="top">
                    All payments are Safe and Secure
                  </b-tooltip>
                </div>
              </b-form-group>
              <div v-if="cardErrors.number" class="text-danger input-error text-right pr-2">
                <small>{{ cardErrors.number }}</small>
              </div>
            </div>
            <div class="cp__form__field__wrapper">
              <div>
                <b-form-group class="cp__form__field form__input mb-0" label-class="cp__form__field__label" label="Expiration" label-for="expiry">
                  <b-form-input id="expiry" ref="cardExpInput" class="form-control"
                                :data-error="(cardErrors.expiry) ? true : false" placeholder="MM/YYYY"
                                v-model.trim="expiry" v-cardformat:formatCardExpiry />
                </b-form-group>
                <div v-if="cardErrors.expiry" class="text-danger input-error text-right pr-2">
                  <small>{{ cardErrors.expiry }}</small>
                </div>
              </div>
              <div v-if="canUpdateCardInfo">
                <b-form-group class="cp__form__field form__input mb-0" label-class="cp__form__field__label" label="Security code" label-for="expiry_month">
                  <b-form-input type="tel" class="form-control" id="cvv_code" ref="cvv_code" :data-error="(cardErrors.cvv_code) ? true : false"
                                placeholder="CVV" pattern="\d*"
                                v-model.trim="cvv_code" v-cardformat:formatCardCVC>
                  </b-form-input>
                  <div class="right-icon">
                    <img id="help" :src="require('@/assets/images/help.svg')"/>
                    <b-tooltip target="help" triggers="hover" placement="top">
                      3 or 4 Digits usually found on the back of your card
                    </b-tooltip>
                  </div>
                </b-form-group>
                <div v-if="cardErrors.cvv_code" class="text-danger input-error text-right pr-2">
                  <small>{{ cardErrors.cvv_code }}</small>
                </div>
              </div>
            </div>
          </div>
          <b-form-group class="mb-0" v-if="!paymentSource.isDefault">
            <b-form-checkbox
              id="default"
              v-model="is_default"
              class="billing-information mb-0"
              name="is_default"
              :value="true"
              :unchecked-value="false"
            >
              Set as default Payment Method
            </b-form-checkbox>
          </b-form-group>
          <b-form-group class="mb-0">
            <b-form-checkbox
              id="billShipSame"
              class="billing-information mb-0"
              v-model="billShipSame"
              name="billShipSame"
              :value="true"
              :unchecked-value="false"
            >
              Billing same as shipping information
            </b-form-checkbox>
          </b-form-group>
          <AddressInfo v-if="!billShipSame" :show-note="showNote" ref="addressInfo" :errors="fieldErrors" />
          <span class="cp-sidebar__note" v-if="showNote">*The changes you make will be effective on your next delivery</span>
          <b-button class="cp-sidebar__btn no-outline-btn mt-0" variant="primary" @click="submit" :disabled="isCPLoading">
            <template v-if="isCPLoading">
              <b-spinner small label="Updating..."></b-spinner>
            </template>
            <template v-else>Update & Save</template>
          </b-button>
          <div class="d-flex justify-content-center privacy-container w-100">
            <img class="privacy-container-img" :src="require('@/assets/images/Secure-Connection.svg')"/>
          </div>
        </b-form>
      </template>
    </div>
  </b-sidebar>
</template>

<script>
import {getHumanReadableDate, isTestCard, updateRequiredFields} from '@/utils'
import {mapGetters, mapState} from 'vuex'
import AddressInfo from '@/components/Order/AddressInfo'
import SidebarHeader from '@/components/Sidebar/SidebarHeader'
import {PaymentUpdateAllowed} from '@/constants'
import ProcessingGif from '@/components/ProcessingGif.vue'

export default {
  name: 'PaymentSidebar',
  components: {ProcessingGif, SidebarHeader, AddressInfo},
  props: {
    paymentSource: Object,
    showNote: Boolean
  },
  data: function() {
    return {
      number: null,
      expiry: null,
      cvv_code: null,
      is_default: false,
      cardErrors: {},
      cardBrand: '',
      billShipSame: true,
      isNew: true,
      expiry_month: null,
      expiry_year: null,
      customer: {
        address1: null,
        address2: null,
        city: null,
        state: null,
        zip: null,
        first_name: null,
        last_name: null,
        email: null,
        phone: null,
        country: 'US',
        ship_address1: null,
        ship_address2: null,
        ship_city: null,
        ship_company: null,
        ship_country: 'US',
        ship_first_name: null,
        ship_last_name: null,
        ship_state: null,
        ship_zip: null
      },
      fieldErrors: {},
      getHumanReadableDate
    }
  },
  computed: {
    ...mapState(['session', 'campaign', 'isLoading', 'isCPLoading']),
    ...mapGetters(['processingGIF']),
    canUpdateCardInfo: function() {
      return this.isNew || (this.paymentSource && this.paymentSource.canUpdate === PaymentUpdateAllowed.YES)
    }
  },
  methods: {
    getCardIcon: function(brand) {
      let cardBrandToClass = {
        'jcb': 'jcb',
        'amex': 'amex',
        'visa': 'visa',
        'unknown': 'credit-card',
        'discover': 'discover',
        'diners': 'diners',
        'mastercard': 'mastercard'
      }
      brand = cardBrandToClass[brand] ? brand : 'unknown'
      return cardBrandToClass[brand]
    },
    validate: function() {
      this.cardErrors = {}
      let hasError = false
      if (this.canUpdateCardInfo) {
        // validate card number
        if (!this.$cardFormat.validateCardNumber(this.number) && !isTestCard(this.number, this.session, this.customer)) {
          this.cardErrors.number = 'Invalid Credit Card Number.'
          hasError = true
        }

        // validate card CVC
        if (!this.$cardFormat.validateCardCVC(this.cvv_code)) {
          this.cardErrors.cvv_code = 'Invalid CVV Code.'
          hasError = true
        }
      }
      if (this.isNew) {
        // validate card first name
        if (this.first_name === null) {
          this.cardErrors.first_name = 'Invalid first name'
          hasError = true
        }

        // validate card last name
        if (this.last_name === null) {
          this.cardErrors.last_name = 'Invalid last name'
          hasError = true
        }
      }

      // validate card expiry
      if (this.expiry === null) {
        this.cardErrors.expiry = 'Invalid Expiration Date'
        hasError = true
      } else {
        this.expiry_month = this.expiry.split(' / ')[0]
        this.expiry_year = this.expiry.split(' / ')[1]

        if (this.expiry_month.length < 2) {
          this.cardErrors.expiry = 'Invalid Month'
          hasError = true
        }

        if (this.expiry_year.length < 4) {
          this.cardErrors.expiry = 'Invalid Year'
          hasError = true
        }

        if (!this.$cardFormat.validateCardExpiry(this.expiry_month, this.expiry_year)) {
          this.cardErrors.expiry = 'Invalid Expiration Date'
          hasError = true
        }
      }

      if (!this.billShipSame) {
        let requiredFields = [
          'address1',
          'city',
          'zip',
          'country'
        ]

        updateRequiredFields(requiredFields, this.customer.country)

        let existingFieldsErrors = {...this.fieldErrors}
        requiredFields.forEach(field => {
          let value = this.customer[field]
          existingFieldsErrors[field] = null
          if (!value || value.length < 1) {
            existingFieldsErrors[field] = 'This field is required'
            hasError = true
          }
        })
        this.fieldErrors = existingFieldsErrors
      }

      return hasError
    },
    submit: function() {
      if (this.$refs.addressInfo) {
        this.customer = {...this.customer, ...this.$refs.addressInfo.customer}
      }

      if (this.validate()) {
        return
      }
      let params = {
        exp_month: this.expiry_month,
        exp_year: this.expiry_year.toString(),
        is_default: !!(this.paymentSource && this.paymentSource.is_default)
      }
      let method = 'add_credit_card'
      if (this.isNew) {
        params['number'] = this.number
        params['cvv_code'] = this.cvv_code
      } else if (this.paymentSource) {
        params['id'] = this.paymentSource.id
        method = 'update_credit_card'
      }

      this.$store.commit('showLoader')

      if (this.billShipSame) {
        this.customer['address1'] = this.customer['ship_address1']
        this.customer['city'] = this.customer['ship_city']
        this.customer['state'] = this.customer['ship_state']
        this.customer['zip'] = this.customer['ship_zip']
      }

      Promise.all([this.$store.dispatch(method, params),
        this.$store.dispatch('update_customer', this.customer)])
        .then((resp) => {
          this.$emit('success')
          this.$store.commit('hideLoader')
        })
        .catch(err => {
          this.$store.commit('hideLoader')
          if (err && err.response && err.response.status === 400) {
            let errors = {}

            if (err.response.data instanceof Array) {
              errors['error'] = err.response.data.join(', ')
            } else {
              Object.keys(err.response.data).forEach(key => {
                errors[key] = err.response.data[key].join(', ')
              })
            }
            this.cardErrors = errors
            this.fieldErrors = errors
          } else {
            this.$store.commit('critical_error', err)
          }
        })
    }
  },
  created() {
    this.expiry = this.paymentSource.expDate
    this.is_default = this.paymentSource.isDefault
    this.isNew = !this.paymentSource.id

    Object.keys(this.customer).forEach(key => {
      this.customer[key] = this.session.customer[key]
    })
  }
}
</script>

<style scoped>

</style>
